
import EleosMap from '@/components/EleosMap.vue';
import LocationTrailerResultsList from './LocationTrailerResultsList.vue';
import { useStore } from 'vuex';
import { ActionTypes } from '@/store/actionTypes';
import { isNullOrUndefined } from '@/shared/helpers';
import { computed, defineComponent, ref } from 'vue';
import Truck from '@/types/Truck';
import TruckMapPoint from '@/types/TruckMapPoint';
import TrailerMapPoint from '@/types/TrailerMapPoint';
import BaseMapPoint from '@/types/BaseMapPoint';
import NearbyTrailerResult from '@/types/NearbyTrailerResult';
import globalConstants from '@/shared/globalConstants';
import SelectedLocationToAttach from './SelectedLocationToAttach.vue';
import DriverFeedback from '@/components/DriverFeedback.vue';
import Geopoint from '@/types/Geopoint';
import Driver from '@/types/Driver';
import { useDebounceFn } from '@vueuse/core';

export default defineComponent({
    name: 'SelectTrailer',
    components: {
        EleosMap,
        LocationTrailerResultsList,
        SelectedLocationToAttach,
        DriverFeedback,
    },
    data(): {
        isRefreshing: boolean;
        isOverlayMinimized: boolean;
        selectedResult: NearbyTrailerResult | null;
    } {
        return {
            isOverlayMinimized: false,
            isRefreshing: false,
            selectedResult: null,
        };
    },
    setup() {
        const store = useStore();

        //Data
        const filteredTrailers = ref<NearbyTrailerResult[] | null>(null);
        const searchFilter = ref<string>('');

        //Computed
        const displayResults = computed(() =>
            filteredTrailers.value === null
                ? allMapResults.value
                : filteredTrailers.value
        );
        const driver = computed<Driver | undefined>(
            () => store.getters.getDriver
        );
        const trailersAtCurrentLocation = computed<
            NearbyTrailerResult[] | undefined
        >(() => store.getters.getTrailersAtCurrentLocation);
        const trailersNearby = computed<NearbyTrailerResult[] | undefined>(
            () => store.getters.getNearbyTrailerResults
        );
        const allMapResults = computed<NearbyTrailerResult[]>(() => {
            if (trailersAtCurrentLocation.value == undefined) return [];

            const allResults = [
                ...trailersAtCurrentLocation.value,
            ] as NearbyTrailerResult[];

            // Includes reserved trailer outside of customer location in location specific results list
            if (trailersNearby.value) {
                trailersNearby.value?.forEach(
                    (nearbyResult: NearbyTrailerResult) => {
                        if (nearbyResult?.trailers[0]?.reservation?.isActive) {
                            allResults.unshift(nearbyResult);
                        }
                    }
                );
            }

            return allResults;
        });

        //Methods
        const debouncedSearchFilterFn = useDebounceFn((value) => {
            searchFilter.value = value;
        }, 500);
        const setSearchFilter = (value: string) => {
            debouncedSearchFilterFn(value);
        };
        const filterTrailers = () => {
            return allMapResults.value.filter((item: NearbyTrailerResult) => {
                const start = 0;
                const end = searchFilter.value.length;
                const [trailer] = item.trailers;

                // We want to be able to search for trailer ids with leading zero(s), without providing a leading zero(s).
                // Ex: 011545 is the trailerId. Entering 11545 would provide a positive match.
                // To accomplish this we test if the searchFilter includes the strippedTrailerId or the original trailerId.
                const strippedTrailerId = trailer.id.replace(/^0+/, "");
                const strippedTrailerIdChunk = strippedTrailerId.substring(start, end);
                const trailerIdChunk = trailer.id.substring(start, end);
                
                return [strippedTrailerIdChunk, trailerIdChunk].includes(searchFilter.value);
            });
        };

        return {
            allMapResults: displayResults,
            driver,
            filteredTrailers,
            searchFilter,
            trailersAtCurrentLocation,
            trailersNearby,
            setSearchFilter,
            filterTrailers,
        };
    },
    computed: {
        loading(): boolean {
            return this.trailersAtCurrentLocation === undefined;
        },
        mapLatitude(): number {
            return this.bestAvailableLocation
                ? this.bestAvailableLocation.lat
                : 39.5; // Geographic center of contiguous US
        },
        mapLongitude(): number {
            return this.bestAvailableLocation
                ? this.bestAvailableLocation.lng
                : -98.35; // Geographic center of contiguous US
        },
        truck(): Truck | undefined {
            return this.$store.getters.getTruck;
        },
        bestAvailableLocation(): Geopoint | undefined {
            return this.$store.getters.getPinnedBestAvailableLocation;
        },
        points(): BaseMapPoint[] {
            const dempoints: BaseMapPoint[] = [];
            if (this.truck && this.bestAvailableLocation) {
                dempoints.push(
                    new TruckMapPoint(this.bestAvailableLocation, this.truck)
                );
            }

            if (this.allMapResults) {
                this.allMapResults?.forEach((result: NearbyTrailerResult) => {
                    dempoints.push(
                        new TrailerMapPoint(result.location, result)
                    );
                });
            }
            return dempoints;
        },
        mapRadius() {
            return globalConstants.map.customerLocationViewRadius;
        },
        noTrailerSelected(): boolean {
            return isNullOrUndefined(this.selectedResult);
        },
        viewName(): string {
            return globalConstants.viewNames.selectTrailer;
        },
    },
    watch: {
        async truck() {
            this.isRefreshing = true;
            await this.$store.dispatch(
                ActionTypes.FETCH_CUSTOMER_AND_TRAILERS_AT_CURRENT_LOCATION,
                undefined
            );
            this.isRefreshing = false;
        },
        searchFilter() {
            if (this.searchFilter === '') {
                this.filteredTrailers = null;
                return;
            }

            this.filteredTrailers = this.filterTrailers();
        },
    },
    methods: {
        setSelectedResult(val: NearbyTrailerResult) {
            this.resetOverlayHeight();
            this.selectedResult = val;
        },
        backToListView(): void {
            this.resetOverlayHeight();
            this.selectedResult = null;
            this.setSearchFilter('');
        },
        minimizeOverlay(element: HTMLElement) {
            if (element.tagName !== 'CANVAS') return;
            this.isOverlayMinimized = true;
        },
        resetOverlayHeight() {
            this.isOverlayMinimized = false;
        },
        async doCustomerAndTrailerRefresh(): Promise<void> {
            await this.$store.dispatch(
                ActionTypes.FETCH_CUSTOMER_AND_TRAILERS_AT_CURRENT_LOCATION,
                undefined
            );
        },
    },
    async mounted() {
        if (this.truck) {
            await this.$store.dispatch(
                ActionTypes.FETCH_CUSTOMER_AND_TRAILERS_AT_CURRENT_LOCATION,
                undefined
            );
        }
    },
});
